import Hammer from 'hammerjs';
import h from 'hyperscript';
import { MEDIA_QUERIES } from 'javascripts/constants';
import TimelineScrollDragger from './TimelineScrollDragger';

export default class TimelineSlider {
  constructor($timeline, mq) {
    this.isDesktop = mq.matches;
    this.viewPortWidth = window.innerWidth;

    this.$timeline = $timeline;
    this.$slider = this.$timeline.querySelector('.timeline__slider');
    this.$contentInner = this.$slider.querySelector('.timeline__slider-items');
    this.$items = this.$slider.querySelectorAll('.timeline__slider-item');
    this.$arrowPrev = this.$slider.querySelector('.timeline__slider-arrow--prev');
    this.$arrowNext = this.$slider.querySelector('.timeline__slider-arrow--next');

    this.itemsLength = this.$items.length;
    this.activeItem = this.$slider.querySelector('.timeline__slider-item--highlighted');
    this.activeIndex = Array.prototype.indexOf.call(this.$items, this.activeItem) || 0;
    this.itemPositions = [];

    this.lastPosX = this.$contentInner.offsetLeft;
    this.isDragging = false;
    this.mc = new Hammer(this.$contentInner);

    this.initArrows();
    this.initItemPosition();
    this.initBox();
    this.initSwipe();

    // for more than 10 items render a scrollbar
    if (this.itemsLength >= 10) {
      this.initScrollBar();
    }

    window.addEventListener('resize', this.handleResize);
    this.$contentInner.addEventListener('focusin', this.handleFocusIn);
    this.$contentInner.addEventListener('click', e => e.preventDefault());
  }

  deconstructor() {
    this.hideArrows();
  }

  /*
  * Removes fallback styling and adds styling for js use
  */
  initBox = () => {
    this.$slider.classList.add('timeline--initialized');
    this.$arrowNext.classList.add('timeline__slider-arrow--visible');
    this.$items.forEach(($item) => {
      $item.setAttribute('tabindex', '0');
    });
    this.initBoxHeight();
  }

  initBoxHeight = () => {
    let highestItemHeight = this.$items[0].offsetHeight;
    this.$items.forEach(($item) => {
      if (highestItemHeight < $item.offsetHeight) {
        highestItemHeight = $item.offsetHeight;
      }
    });
    // IE needs to have a specific height on every item
    this.$items.forEach(($item) => {
      $item.setAttribute('style', `height: ${Math.ceil(highestItemHeight)}px`);
    });
    // Other Browsers only need the container to have the right height
    this.$contentInner.setAttribute('style', `height: ${(Math.ceil(highestItemHeight * 1.1))}px`);
  }

  /*
  * Adds click events to arrows
  */
  initArrows = () => {
    this.handleArrowVisibility();
    this.$arrowPrev.addEventListener('click', this.handlePrevItem);
    this.$arrowNext.addEventListener('click', this.handleNextItem);
  }

  /*
  * Map item positions to indexes for snappy behaviour
  */
  initItemPosition = () => {
    let spaceAdd = 0;
    this.$items.forEach((item, index) => {
      this.itemPositions[index] = -1 * spaceAdd;
      spaceAdd += item.offsetWidth;
    });
  }

  /*
  * Initiate the draggable content
  */
  initSwipe = (destroy) => {
    if (destroy) {
      this.mc.destroy();
      this.mc = new Hammer(this.$contentInner);
    }

    this.mc.add(new Hammer.Pan({
      direction: Hammer.DIRECTION_ALL,
      threshold: 0,
    }));

    this.mc.on('pan', this.handleDrag);
  }

  /*
  * Initiate the scrollbar to scroll through the slider faster
  */
  initScrollBar = () => {
    const $scrollbar = this.$slider.querySelector('.timeline__scrollbar');
    if ($scrollbar) {
      $scrollbar.parentNode.removeChild($scrollbar);
    }

    this.$scrollDragger = h('.timeline__scroll-dragger');
    this.$scrollBar = h('.timeline__scrollbar', h('.timeline__scroll-track'), this.$scrollDragger);
    this.$timeline.insertBefore(this.$scrollBar, this.$slider);

    this.ScrollDragger = new TimelineScrollDragger(this.$timeline, this.itemsLength);
    this.$scrollDragger.addEventListener('scrollDraggerDrag', this.handleScrollDraggerDrag);
    this.$scrollDragger.addEventListener('scrollDraggerDragEnd', this.handleScrollDraggerDragEnd);
  }

  /*
  * Event is fired while scroll dragger is being dragged
  */
  handleScrollDraggerDrag = ({ detail: { index } }) => {
    this.centerSelectedItem(index);
  }

  /*
  * Event is fired when scroll dragger was dragged to a point
  */
  handleScrollDraggerDragEnd = ({ detail: { index } }) => {
    this.centerSelectedItem(index);
    this.highlightSelectedItem(this.$items[index]);
    this.handleArrowVisibility();
  }

  /*
  * Handle behaviour for custom focusIn event (reacts to click and tab)
  * Finds the closest slide item, centers and highlights it
  */
  handleFocusIn = (event) => {
    const $slide = event.target.closest('.timeline__slider-item');
    this.$contentInner.scrollTop = 0;
    const index = Array.prototype.indexOf.call(this.$items, $slide);
    this.centerSelectedItem(index);
    this.highlightSelectedItem(this.$items[index]);
    this.handleArrowVisibility();
    if (this.ScrollDragger) {
      this.ScrollDragger.updateScrollDraggerByIndex(index);
    }
  }

  /*
  * Handle positioning of the innerContent when dragged by swiping.
  */
  handleDrag = (event) => {
    if (!this.isDragging) {
      this.isDragging = true;
    }
    const posX = event.deltaX + this.lastPosX;
    this.$contentInner.style.transform = `translateX(${posX}px)`;

    if (event.isFinal) {
      this.isDragging = false;
      this.snapToClosestItem(posX);
      this.handleArrowVisibility();
      this.lastPosX = posX;
    }
  }

  /*
  * Translates the center of the selected Item to the center of the containerInner
  * @param: index - index of selected item of items array
  */
  centerSelectedItem = (index) => {
    const itemPosition = this.itemPositions[index];
    const containerCenter = this.$contentInner.parentNode.offsetWidth / 2;
    const activeIndex = this.itemPositions.indexOf(itemPosition);
    const itemCenter = this.$items[activeIndex].offsetWidth / 2;
    const lastItem = this.$items[this.itemsLength - 1];
    const maxItemPos = this.isDesktop ?
      this.itemPositions[this.itemsLength - 2] :
      this.itemPositions[this.itemsLength - 1];
    const lastIsInView = lastItem.getBoundingClientRect().right <= this.$contentInner.offsetWidth;

    const maxCenteredPosition = (maxItemPos + containerCenter) - itemCenter;
    const centeredPosition = (itemPosition + containerCenter) - itemCenter;
    let finalPosition = centeredPosition;

    if ((centeredPosition > 0) && !lastIsInView) {
      // do not translate lower than the start item
      finalPosition = 0;
    } else if (
      lastIsInView &&
      (centeredPosition < this.lastPosX)) {
      // do not translate higher than the last item
      finalPosition = this.lastPosX;
    } else if (centeredPosition < maxCenteredPosition) {
      // do not translate over the maximum scrolling point
      finalPosition = maxCenteredPosition;
    }

    this.$contentInner.style.transform = `translateX(${finalPosition}px)`;
    this.activeIndex = activeIndex;
    this.lastPosX = finalPosition;
    this.handleArrowVisibility();
  }

  /*
  * Translates to the closest item to a given position
  * @param: position - drag position
  */
  snapToClosestItem = (position) => {
    const lastItemPos = this.itemPositions[this.itemPositions.length - 1];
    const lastItem = this.$items[this.itemsLength - 1];
    const containerWidth = this.$contentInner.parentNode.offsetWidth;
    const maxSnapPosition = (lastItemPos - lastItem.offsetWidth) + containerWidth;
    const minSnapPosition = this.itemPositions[0];

    let snapPosition = this.getClosestItemPosition(position);
    if (window.matchMedia(MEDIA_QUERIES.s).matches) {
      // disable snapping for very small screens
      snapPosition = position;
    }
    if (snapPosition < maxSnapPosition) {
      snapPosition = maxSnapPosition;
    }
    if (snapPosition > minSnapPosition) {
      snapPosition = minSnapPosition;
    }

    this.$contentInner.style.transform = `translateX(${snapPosition}px)`;
    this.lastPosX = snapPosition;
  }

  /*
  * Returns the item position with the lowest distance to the current drag position
  */
  getClosestItemPosition = (position) => {
    let curr = this.lastPosX;
    let diff = Math.abs((-1 * position) - curr);

    for (let i = 0; i < this.itemPositions.length; i += 1) {
      const newDiff = Math.abs(position - this.itemPositions[i]);

      if (newDiff < diff) {
        diff = newDiff;
        curr = this.itemPositions[i];
      }
    }

    return curr;
  }

  /*
  * Clickhandler for prev arrow.
  * Highlights the previous item.
  */
  handlePrevItem = () => {
    if (this.activeIndex > 0) {
      this.activeIndex -= 1;
      this.centerSelectedItem(this.activeIndex);
      this.highlightSelectedItem(this.$items[this.activeIndex]);
      if (this.ScrollDragger) {
        this.ScrollDragger.updateScrollDraggerByIndex(this.activeIndex);
      }
    }
  }

  /*
  * Clickhandler for next arrow.
  * Highlights the next item.
  */
  handleNextItem = () => {
    if (this.activeIndex < this.itemsLength) {
      this.activeIndex += 1;
      this.centerSelectedItem(this.activeIndex);
      this.highlightSelectedItem(this.$items[this.activeIndex]);
      if (this.ScrollDragger) {
        this.ScrollDragger.updateScrollDraggerByIndex(this.activeIndex);
      }
    }
  }

  /*
  * Recalculates the item positions on resize and
  * snaps to the closest item.
  */
  handleResize = () => {
    if (window.innerWidth !== this.viewPortWidth) {
      this.viewPortWidth = window.innerWidth;
      this.activeIndex = 0;
      this.initItemPosition();
      this.initBoxHeight();
      this.initSwipe(true);
      this.centerSelectedItem(0);
      this.highlightSelectedItem(this.$items[0]);
      this.handleArrowVisibility();
    }
  }

  /*
  * Hides and shows arrows only when needed.
  */
  handleArrowVisibility = () => {
    const lastItemPos = this.itemPositions[this.itemPositions.length - 1];
    const lastItem = this.$items[this.itemsLength - 1];
    const containerWidth = this.$contentInner.offsetWidth;
    const maxSnapPosition = (lastItemPos - lastItem.offsetWidth) + containerWidth;
    if ((this.activeIndex === 0 && this.isDesktop) ||
        (this.lastPosX === 0 && !this.isDesktop)) {
      this.showElement(this.$arrowPrev, false);
      this.showElement(this.$arrowNext, true);
    } else if (((this.activeIndex === this.itemsLength - 1) && this.isDesktop) ||
                (this.lastPosX <= maxSnapPosition && !this.isDesktop)) {
      this.showElement(this.$arrowPrev, true);
      this.showElement(this.$arrowNext, false);
    } else {
      this.showElement(this.$arrowPrev, true);
      this.showElement(this.$arrowNext, true);
    }
  }

  /*
  * Hides arrows.
  */
  hideArrows = () => {
    this.showElement(this.$arrowPrev, false);
    this.showElement(this.$arrowNext, false);
  }

  /*
  * Adds a class to an element that handles visibility.
  */
  showElement = ($el, showElement) => {
    const styleClass = 'timeline__slider-arrow--visible';
    if (showElement) {
      $el.classList.add(styleClass);
    } else {
      $el.classList.remove(styleClass);
    }
  }

  highlightSelectedItem = ($el) => {
    if ($el) {
      // Remove all highlights
      this.$items.forEach(($item) => {
        $item.classList.remove('timeline__slider-item--highlighted');
        $item.removeAttribute('aria-selected');
      });

      this.throwTabEvent($el);
      $el.classList.add('timeline__slider-item--highlighted');
    }
  }

  throwTabEvent = ($target) => {
    const selectTabEvent = new CustomEvent('selectTab', {
      bubbles: true,
    });
    $target.dispatchEvent(selectTabEvent);
  }
}
